import React from 'react'

import { graphql } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import styled from 'styled-components'

import Breadcrumbs from '../../components/core/Breadcrumbs'
import Layout from '../../components/core/layout'
import SEO from '../../components/core/seo'
import HeroInternal from '../../components/elements/HeroInternal'
import ChevronBackground from '../../components/reusables/ChevronBackground'
import { above } from '../../style/design-system'
import emails from '../../utils/static-data/emails'

const CommunityOutreach = ({ className, data, pageContext }) => (
  <Layout>
    <SEO
      title="Community Outreach | Giving Back"
      description="UMA is committed to making a positive impact in our community. Learn about our affiliation with children&#039;s hospitals, fighting cancer and more."
    />

    <div className={className}>
      <HeroInternal
        backgroundImage={{
          image: data.heroImgLg.childImageSharp.gatsbyImageData,
          objectPosition: 'center center',
        }}
        backgroundImageMobile={{
          image: data.heroImgSm.childImageSharp.gatsbyImageData,
          objectPosition: 'center center',
        }}
        contentLg="Community Involvement"
        contentSm="Giving back helps communities move forward."
      />
      <main>
        <div className="container">
          <div className="row">
            <div className="col-lg-10 offset-lg-1">
              <Breadcrumbs pageContext={pageContext} />
            </div>
          </div>
        </div>

        <div className="container mt-6 mb-9 mb-lg-10">
          <div className="row">
            <div className="col-lg-10 offset-lg-1">
              <p>
                We are proud to partner with nonprofit organizations that align with our mission to equip and empower
                students to excel in healthcare careers. Our community involvement efforts focus on three important
                areas: healthcare, education, and diversity and inclusion.
              </p>
            </div>
          </div>
          <div className="text-center flex-center">
            <StaticImage
              src="../../assets/images/community-outreach/american-cancer-society.jpg"
              alt="American Cancer Society Logo"
              className="mx-6 my-4"
              placeholder="none"
            />
            <StaticImage
              src="../../assets/images/community-outreach/big-brothers-big-sisters.jpg"
              alt="Big Brothers Big Sisters Logo"
              className="mx-6 my-4"
              placeholder="none"
            />
            <StaticImage
              src="../../assets/images/community-outreach/casa.png"
              alt="CASA Logo"
              className="mx-6 my-4"
              placeholder="none"
            />
            <StaticImage
              src="../../assets/images/community-outreach/feeding-tampa-bay.png"
              alt="Feeding Tampa Bay Logo"
              className="mx-6 my-4"
              placeholder="none"
            />
            <StaticImage
              src="../../assets/images/community-outreach/oneblood.jpg"
              alt="Oneblood Logo"
              className="mx-6 my-4"
              placeholder="none"
            />
            <StaticImage
              src="../../assets/images/community-outreach/st-joes.jpg"
              alt="St. Joseph's Children's Hospital Logo"
              className="mx-6 my-4"
              placeholder="none"
            />
          </div>
        </div>

        <div className="community-impact-report container pb-6">
          <div className="row">
            <div className="col-12 col-md-4 col-lg-6 poster-div">
              <a href="https://online.flippingbook.com/view/159913929/" rel="noopener noreferrer" target="_blank">
                <StaticImage
                  src="../../assets/images/community-outreach/community-impact-report-22-23.webp"
                  alt="UMA Community Impact Report"
                  className="mx-6 my-4 poster"
                  placeholder="none"
                />
              </a>
            </div>
            <div className="col-12 col-md-8 col-lg-6">
              <p>
                Care is at the core of everything we do at Ultimate Medical Academy. We believe that care is contagious
                and that, ultimately, care pays back. It starts with care for our team members, who in turn care for our
                students and graduates. Our learners go on to pursue careers with our employer-partners, helping to care
                for others as healthcare professionals and so on. Team UMA also extends care into our communities
                through volunteerism and by championing causes that align with our mission and culture, including our
                steadfast commitment to fostering diversity, equity and inclusion. This is the ripple effect of care,
                and it’s growing!
              </p>

              <p>
                <a href="https://online.flippingbook.com/view/159913929/" rel="noopener noreferrer" target="_blank">
                  UMA's 2022/2023 Community Impact Report
                </a>
              </p>
              <h2 className="mt-12">Past Community Impact Reports</h2>
              <a href="/pdfs/UMA_Community_Impact_Report.pdf" rel="noopener noreferrer" target="_blank">
                2021 Communicty Impact Report
              </a>
            </div>
          </div>
        </div>

        <ChevronBackground backgroundColor="blue" direction="north" className="text-white-force py-9 py-lg-10">
          <div className="container text-lg-center">
            <div className="row">
              <div className="col-lg-10 offset-lg-1">
                <h2>Contact Us</h2>
                <p>
                  We regularly evaluate nonprofit community involvement sponsorship opportunities for philanthropic
                  support and voluntary faculty and staff participation. To request UMA support or to contact our
                  community involvement representatives, please email:{' '}
                  <a href={`mailto:${emails.umaMedia}`}>{emails.umaMedia}</a>.
                </p>
              </div>
            </div>
          </div>
        </ChevronBackground>
      </main>
    </div>
  </Layout>
)

export default styled(CommunityOutreach)`
  .flex-center {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
  }
  .poster-div {
    text-align: center;
    ${above.lg`
      .poster {
        width: 80%;
      }
    `}
  }
`

export const query = graphql`
  {
    heroImgLg: file(absolutePath: { regex: "/community-outreach/hero-lg.jpg/" }) {
      childImageSharp {
        gatsbyImageData(quality: 85, width: 1278, placeholder: NONE, layout: FIXED)
      }
    }
    heroImgSm: file(absolutePath: { regex: "/community-outreach/hero-sm.jpg/" }) {
      childImageSharp {
        gatsbyImageData(quality: 85, width: 375, placeholder: NONE, layout: FIXED)
      }
    }
  }
`
